import React, { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw, AlertCircle, CheckCircle2, Mail, Fingerprint, Shield, X } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * RecentAdminActivityCard — live timeline of admin-auth events
 * (sign-ins, 2FA sends, magic links, IP blocks, revokes, failures).
 * Master-admin only — renders nothing for other users.
 */
const statusMeta = (status, reason) => {
  const r = reason || '';
  if (status === 'success') {
    if (r === 'magic_link')             return { color: '#2EAA5C', icon: <Mail size={12} />, label: 'Magic-link sign-in' };
    if (r === 'passkey')                return { color: '#2EAA5C', icon: <Fingerprint size={12} />, label: 'Fingerprint sign-in' };
    if (r === 'trusted_device_cookie')  return { color: '#2EAA5C', icon: <CheckCircle2 size={12} />, label: 'Trusted-device sign-in' };
    return { color: '#2EAA5C', icon: <CheckCircle2 size={12} />, label: 'Sign-in' };
  }
  if (status === 'fail') {
    if (r.startsWith('magic_'))         return { color: '#DC2626', icon: <AlertCircle size={12} />, label: `Magic link: ${r.replace('magic_', '')}` };
    if (r === 'rate_limited')           return { color: '#DC2626', icon: <AlertCircle size={12} />, label: 'Rate-limited' };
    if (r === 'wrong_password')         return { color: '#DC2626', icon: <AlertCircle size={12} />, label: 'Wrong password' };
    if (r === 'wrong_email')            return { color: '#DC2626', icon: <AlertCircle size={12} />, label: 'Unknown email' };
    if (r === 'bad_2fa_code')           return { color: '#DC2626', icon: <AlertCircle size={12} />, label: 'Bad 2FA code' };
    return { color: '#DC2626', icon: <AlertCircle size={12} />, label: `Failed: ${r || 'unknown'}` };
  }
  if (status === 'pending_2fa')       return { color: '#F59E0B', icon: <Shield size={12} />, label: '2FA code sent' };
  if (status === 'magic_sent')        return { color: '#F59E0B', icon: <Mail size={12} />, label: 'Magic link emailed' };
  if (status === 'passkey_register_begin') return { color: '#F59E0B', icon: <Fingerprint size={12} />, label: 'Passkey enrollment started' };
  if (status === 'passkey_registered')     return { color: '#2EAA5C', icon: <Fingerprint size={12} />, label: 'Passkey registered' };
  if (status === 'passkey_deleted')        return { color: '#6B6B6B', icon: <Fingerprint size={12} />, label: 'Passkey removed' };
  if (status === 'sessions_revoked')       return { color: '#7B4FD8', icon: <Shield size={12} />, label: 'Sessions revoked' };
  if (status === 'ip_blocked_by_alert')    return { color: '#DC2626', icon: <Shield size={12} />, label: 'IP blocked via alert' };
  return { color: '#6B6B6B', icon: <Activity size={12} />, label: status };
};

const timeAgo = (iso) => {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const RecentAdminActivityCard = () => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!apiClient.getToken()) { setLoading(false); return; }
    try {
      const d = await apiClient.request('/api/auth/admin-direct/activity-feed?limit=25');
      setEvents(d.events || []);
      setError(null);
    } catch (e) {
      // Preserve the previous "silently hide for non-master admins" branch:
      // backend returns HTTP 403 with detail "Master admin only", which
      // apiClient.request() surfaces as the Error message verbatim.
      if (e && e.message === 'Master admin only') { setError('not_master'); }
      else { setError((e && e.message) || 'Error'); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Hide entirely for non-master admins
  if (error === 'not_master') return null;

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-4 animate-pulse" data-testid="admin-activity-loading" style={{ borderColor: '#E0E0E0' }}>
        <div className="h-4 w-36 bg-gray-200 rounded mb-3" />
        <div className="h-3 w-full bg-gray-100 rounded mb-2" />
        <div className="h-3 w-2/3 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!events) {
    return (
      <div className="bg-white rounded-lg border p-4" data-testid="admin-activity-error" style={{ borderColor: '#E0E0E0' }}>
        <div className="flex items-center gap-2 text-sm text-red-700"><AlertCircle size={14} /> {error || 'Failed to load'}</div>
      </div>
    );
  }

  const shown = expanded ? events : events.slice(0, 6);

  return (
    <div className="bg-white rounded-lg border" data-testid="admin-activity-card" style={{ borderColor: '#E0E0E0' }}>
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#F0F0F0' }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(123,79,216,0.1)' }}>
            <Activity size={16} style={{ color: '#7B4FD8' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Recent Admin Activity</h3>
            <p className="text-xs text-gray-500">Last {events.length} auth events</p>
          </div>
        </div>
        <button onClick={load} className="text-gray-400 hover:text-gray-700 p-1 rounded" title="Refresh" data-testid="admin-activity-refresh">
          <RefreshCw size={14} />
        </button>
      </div>
      <div className="divide-y" style={{ borderColor: '#F5F5F5' }}>
        {shown.length === 0 && (
          <div className="px-4 py-6 text-sm text-gray-500 text-center">No recent activity.</div>
        )}
        {shown.map((e, i) => {
          const m = statusMeta(e.status, e.reason);
          return (
            <div key={i} className="px-4 py-2 flex items-center gap-3" data-testid={`admin-activity-row-${i}`}>
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${m.color}1a`, color: m.color }}
              >
                {m.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold" style={{ color: '#1A1209' }}>{m.label}</div>
                <div className="text-[11px] text-gray-500 truncate">
                  <span className="font-mono">{e.ip || '—'}</span>
                  {e.email && e.email !== '-' && <span> · {e.email}</span>}
                </div>
              </div>
              <div className="text-[11px] text-gray-400 whitespace-nowrap">{timeAgo(e.timestamp)}</div>
            </div>
          );
        })}
      </div>
      {events.length > 6 && (
        <div className="px-4 py-2 border-t" style={{ borderColor: '#F0F0F0' }}>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-gray-600 hover:text-gray-900"
            data-testid="admin-activity-toggle"
          >
            {expanded ? <>Show less <X size={10} className="inline" /></> : `Show all ${events.length} events`}
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentAdminActivityCard;
